// src/stage2/characters/MainCharacterPlayer.jsx
import * as THREE from 'three';
import React, { useMemo, useRef, useEffect } from 'react';
import { useThree, useFrame } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import { SkeletonUtils } from 'three-stdlib';
import { groundMeshes } from '../collision/CollisionStore';

/* ---------- util: center + feet-on-ground (no auto height) ---------- */
function fitAndGround(object) {
  const box = new THREE.Box3().setFromObject(object);
  const center = box.getCenter(new THREE.Vector3());
  object.position.sub(center);
  box.setFromObject(object);
  object.position.y -= box.min.y;
}

/* ---------- util: prep shadows ---------- */
function prepareForShadows(root, { shadowOnly = true } = {}) {
  root.traverse((obj) => {
    if (!obj.isMesh) return;

    obj.castShadow = true;
    obj.receiveShadow = false;

    if (shadowOnly && obj.material) {
      const hide = (m) => {
        m.transparent = true;
        m.opacity = 0.0;
        m.colorWrite = false;
        m.depthWrite = true;
        m.depthTest = true;
        m.shadowSide = THREE.FrontSide;
      };
      (Array.isArray(obj.material) ? obj.material : [obj.material]).forEach(hide);
    }

    const isSkinned = !!obj.isSkinnedMesh;
    const hasMorphs = !!obj.morphTargetInfluences;

    const depthMat = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking });
    depthMat.skinning = isSkinned;
    depthMat.morphTargets = hasMorphs;
    depthMat.morphNormals = hasMorphs;
    obj.customDepthMaterial = depthMat;

    const distanceMat = new THREE.MeshDistanceMaterial();
    distanceMat.skinning = isSkinned;
    distanceMat.morphTargets = hasMorphs;
    distanceMat.morphNormals = hasMorphs;
    obj.customDistanceMaterial = distanceMat;

    if (obj.isSkinnedMesh) obj.frustumCulled = false;
  });
}

export default function MainCharacterPlayer({
  baseScale = 0.18,
  offsetForward = 0.4,
  shadowScaleXZ = [1.6, 1.4],

  idleUrl = '/stageAssets/stage2/MainCharacterShadow.glb',

  isWalking = false,
  idleClipName = 'BreathingIdle',
  walkLoopClip = 'WalkingInPlace',
  animSpeed = 1.0,

  greetingClipName = 'Greeting',
  danceClipNames = ['Dance-Samba', 'DanceTutHipHop', 'Jumping', 'Dance-SlideHipHop'],
  idleSpecialDelay = 2.5,
}) {
  const { camera } = useThree();
  const group = useRef();
  const raycasterRef = useRef(new THREE.Raycaster());

  const forwardRef = useRef(new THREE.Vector3());
  const eulerRef = useRef(new THREE.Euler(0, 0, 0, 'YXZ'));

  const gltf = useGLTF(idleUrl);

  // ✅ IMPORTANT: stabilize arrays so effects don't re-run every render
  const shadowX = shadowScaleXZ?.[0] ?? 1.6;
  const shadowZ = shadowScaleXZ?.[1] ?? 1.4;

  const danceKey = useMemo(() => (danceClipNames || []).join('|'), [danceClipNames]);
  const danceNamesStable = useMemo(
    () => (danceClipNames ? [...danceClipNames] : []),
    [danceKey]
  );

  const mixerRef = useRef(null);
  const idleActionRef = useRef(null);
  const walkActionRef = useRef(null);
  const greetingActionRef = useRef(null);
  const danceActionsRef = useRef([]);

  const modeRef = useRef('idle');
  const idleStartTimeRef = useRef(null);
  const hasGreetedRef = useRef(false);
  const isWalkingRef = useRef(isWalking);

  const onFinishedRef = useRef(null);
  const FADE = 0.15;

  const clonedScene = useMemo(() => {
    if (!gltf?.scene) return null;
    const cloned = SkeletonUtils.clone(gltf.scene);

    fitAndGround(cloned);
    prepareForShadows(cloned, { shadowOnly: true });

    cloned.scale.set(baseScale * shadowX, baseScale, baseScale * shadowZ);
    return cloned;

    // ✅ Only depend on primitives, NOT arrays
  }, [gltf, baseScale, shadowX, shadowZ]);

  const stopSpecials = () => {
    if (greetingActionRef.current) {
      greetingActionRef.current.stop();
      greetingActionRef.current.enabled = false;
    }
    if (danceActionsRef.current?.length) {
      danceActionsRef.current.forEach((a) => {
        a.stop();
        a.enabled = false;
      });
    }
  };

  // ✅ Setup mixer/actions ONCE per clonedScene + relevant string deps
  useEffect(() => {
    if (!clonedScene || !gltf?.animations?.length) {
      console.warn('[MainCharacterPlayer] No animations found on GLB');
      return;
    }

    const mixer = new THREE.AnimationMixer(clonedScene);
    mixerRef.current = mixer;

    const clips = gltf.animations;

    const idleClip = clips.find((c) => c.name === idleClipName) || clips[0];
    const walkClip = clips.find((c) => c.name === walkLoopClip) || clips[0];

    console.log('[MainCharacterPlayer] using idle clip:', idleClip.name);
    console.log('[MainCharacterPlayer] using walk clip:', walkClip.name);

    const idleAction = mixer.clipAction(idleClip);
    const walkAction = mixer.clipAction(walkClip);

    idleAction.timeScale = animSpeed;
    walkAction.timeScale = animSpeed;

    idleAction.setLoop(THREE.LoopRepeat, Infinity);
    walkAction.setLoop(THREE.LoopRepeat, Infinity);

    idleAction.enabled = true;
    walkAction.enabled = true;
    idleAction.setEffectiveWeight(1);
    walkAction.setEffectiveWeight(1);

    idleActionRef.current = idleAction;
    walkActionRef.current = walkAction;

    // greeting
    const greetClip = clips.find((c) => c.name === greetingClipName);
    if (greetClip) {
      const a = mixer.clipAction(greetClip);
      a.setLoop(THREE.LoopOnce, 1);
      a.clampWhenFinished = true;
      a.enabled = false;
      a.timeScale = animSpeed;
      greetingActionRef.current = a;
      console.log('[MainCharacterPlayer] using greeting clip:', greetClip.name);
    } else {
      greetingActionRef.current = null;
    }

    // dances
    danceActionsRef.current = danceNamesStable
      .map((name) => {
        const clip = clips.find((c) => c.name === name);
        if (!clip) return null;
        const a = mixer.clipAction(clip);
        a.setLoop(THREE.LoopOnce, 1);
        a.clampWhenFinished = true;
        a.enabled = false;
        a.timeScale = animSpeed;
        return a;
      })
      .filter(Boolean);

    if (danceActionsRef.current.length) {
      console.log('[MainCharacterPlayer] dance clips:', danceActionsRef.current.map((a) => a.getClip().name));
    }

    // start idle
    stopSpecials();
    walkAction.stop();
    idleAction.reset().fadeIn(FADE).play();
    modeRef.current = 'idle';
    idleStartTimeRef.current = performance.now() / 1000;

    onFinishedRef.current = () => {
      if (modeRef.current === 'special' && idleActionRef.current) {
        stopSpecials();
        idleActionRef.current.reset().fadeIn(FADE).play();
        modeRef.current = 'idle';
        idleStartTimeRef.current = performance.now() / 1000;
      }
    };

    mixer.addEventListener('finished', onFinishedRef.current);

    return () => {
      try {
        if (onFinishedRef.current) mixer.removeEventListener('finished', onFinishedRef.current);
      } catch {}
      stopSpecials();
      idleAction.stop();
      walkAction.stop();
      mixer.stopAllAction();

      mixerRef.current = null;
      idleActionRef.current = null;
      walkActionRef.current = null;
      greetingActionRef.current = null;
      danceActionsRef.current = [];
    };
  }, [
    clonedScene,
    gltf,
    idleClipName,
    walkLoopClip,
    greetingClipName,
    animSpeed,
    danceKey, // ✅ stable string instead of array reference
  ]);

  // walk/idle switch
  useEffect(() => {
    isWalkingRef.current = isWalking;

    const idle = idleActionRef.current;
    const walk = walkActionRef.current;
    if (!idle || !walk) return;

    if (isWalking) {
      if (modeRef.current === 'special') stopSpecials();

      modeRef.current = 'walk';
      idleStartTimeRef.current = null;

      walk.reset();
      walk.enabled = true;
      walk.setEffectiveWeight(1);
      walk.fadeIn(FADE).play();

      idle.fadeOut(FADE);
    } else {
      modeRef.current = 'idle';

      idle.reset();
      idle.enabled = true;
      idle.setEffectiveWeight(1);
      idle.fadeIn(FADE).play();

      walk.fadeOut(FADE);

      idleStartTimeRef.current = performance.now() / 1000;
    }
  }, [isWalking]);

  useFrame((_, delta) => {
    const mixer = mixerRef.current;
    if (mixer) mixer.update(delta); // ✅ raw delta

    // idle specials
    const now = performance.now() / 1000;
    if (
      !isWalkingRef.current &&
      modeRef.current === 'idle' &&
      idleStartTimeRef.current != null &&
      now - idleStartTimeRef.current > idleSpecialDelay
    ) {
      const idle = idleActionRef.current;
      const greet = greetingActionRef.current;
      const dances = danceActionsRef.current;

      let actionToPlay = null;

      if (!hasGreetedRef.current && greet) {
        hasGreetedRef.current = true;
        actionToPlay = greet;
      } else if (dances && dances.length > 0) {
        actionToPlay = dances[Math.floor(Math.random() * dances.length)];
      }

      if (actionToPlay && idle) {
        modeRef.current = 'special';
        idleStartTimeRef.current = null;

        idle.fadeOut(FADE);

        actionToPlay.reset();
        actionToPlay.enabled = true;
        actionToPlay.setEffectiveWeight(1);
        actionToPlay.fadeIn(FADE).play();
      } else {
        idleStartTimeRef.current = now + 9999;
      }
    }

    if (!group.current) return;

    const dist = Math.abs(offsetForward);

    const forward = forwardRef.current;
    camera.getWorldDirection(forward);
    forward.y = 0;
    if (forward.lengthSq() === 0) forward.set(0, 0, 1);
    forward.normalize();

    const baseX = camera.position.x;
    const baseZ = camera.position.z;

    const offset = forward.clone().multiplyScalar(dist);
    const targetX = baseX + offset.x;
    const targetZ = baseZ + offset.z;

    let groundY = 0;
    if (groundMeshes.length > 0) {
      const raycaster = raycasterRef.current;
      const origin = new THREE.Vector3(targetX, 100, targetZ);
      raycaster.set(origin, new THREE.Vector3(0, -1, 0));
      const hits = raycaster.intersectObjects(groundMeshes, true);
      if (hits.length > 0) groundY = hits[0].point.y;
    }

    group.current.position.set(targetX, groundY, targetZ);

    const euler = eulerRef.current;
    euler.setFromQuaternion(camera.quaternion, 'YXZ');
    group.current.rotation.y = euler.y + Math.PI;
  });

  return clonedScene ? (
    <group ref={group}>
      <primitive object={clonedScene} />
    </group>
  ) : null;
}

useGLTF.preload('/stageAssets/stage2/MainCharacterShadow.glb');
